import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, order: null });
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session id provided" });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const d = res.data;
        if (d.payment_status === "paid") {
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          setState({ status: "paid", payment_status: d.payment_status, order: d.order });
          return;
        }
        if (d.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setState({ status: "pending", order: d.order });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setState({ status: "error", message: "Could not verify payment status" });
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 text-[var(--navy)] mx-auto animate-spin"/>
          <h1 className="font-serif-display text-4xl text-[var(--navy)] mt-6">Verifying payment…</h1>
          <p className="text-[var(--ink-soft)] mt-2">Hang tight, this only takes a moment.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-[var(--crimson)] text-[var(--bone)] grid place-items-center mx-auto">
            <CheckCircle2 className="w-9 h-9"/>
          </div>
          <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)] mt-6" data-testid="success-status">ORDER CONFIRMED</div>
          <h1 className="font-serif-display text-5xl text-[var(--navy)] mt-3">Thank you kindly.</h1>
          <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
            Papa&apos;s firing up the press. We&apos;ll email you tracking once it ships from the shop.
          </p>
          {state.order && (
            <div className="card-rustic p-6 mt-8 text-left">
              <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">ORDER #</div>
              <div className="font-mono-ui text-sm mt-1 break-all" data-testid="success-order-id">{state.order.id}</div>
              <div className="font-serif-display text-2xl text-[var(--crimson)] mt-4">${state.order.total?.toFixed(2)}</div>
            </div>
          )}
          <Link to="/shop" className="btn-primary mt-8 inline-flex">Back to Shop</Link>
        </>
      )}
      {state.status === "pending" && (
        <>
          <h1 className="font-serif-display text-4xl text-[var(--navy)]">Still processing…</h1>
          <p className="text-[var(--ink-soft)] mt-2">Your payment is taking a bit longer. We&apos;ll email you a confirmation.</p>
          <Link to="/" className="btn-ghost mt-6 inline-flex">Back to Home</Link>
        </>
      )}
      {state.status === "expired" && (
        <>
          <h1 className="font-serif-display text-4xl text-[var(--navy)]">Session expired.</h1>
          <p className="text-[var(--ink-soft)] mt-2">Your payment session timed out. Try again from your cart.</p>
          <Link to="/cart" className="btn-primary mt-6 inline-flex">Back to Cart</Link>
        </>
      )}
      {state.status === "error" && (
        <>
          <h1 className="font-serif-display text-4xl text-[var(--navy)]">Something went sideways.</h1>
          <p className="text-[var(--ink-soft)] mt-2">{state.message}</p>
          <Link to="/" className="btn-ghost mt-6 inline-flex">Back to Home</Link>
        </>
      )}
    </div>
  );
}
